import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Clock, Calendar } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { sendEmail } from "@/lib/email";
import { trackEvent } from "@/lib/analytics";
import { seo } from "@/lib/seo";
import { faqSchema } from "@/lib/seo-schemas";

const CONTACT_FAQS = [
  {
    question: "What does Currency Thoughts actually do?",
    answer:
      "We are an independent insurance agency. We help families think through protection, accumulation, and transfer — the questions behind the paperwork — with education first and a consultation when you want one.",
  },
  {
    question: "Is a consultation free?",
    answer:
      "Yes. Initial consultations are educational and carry no obligation. We listen first and share frameworks.",
  },
  {
    question: "Which states do you serve?",
    answer:
      "Product availability varies by state and carrier. Reach out and we'll confirm what is available for your household.",
  },
];

export const Route = createFileRoute("/contact")({
  head: () =>
    seo({
      title: "Contact · Currency Thoughts",
      description:
        "Speak with the Currency Thoughts team about family legacy strategy or Strategist opportunities. We respond calmly, generationally, and on the record.",
      canonical: "/contact",
      schema: faqSchema(CONTACT_FAQS),
    }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* SECTION 1 — Hero */}
        <section className="bg-navy-900 text-cream-50 py-20">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <Eyebrow color="bronze" align="center">Get in Touch</Eyebrow>
            <h1
              className="mt-6 font-display font-bold text-cream-50"
              style={{ fontSize: "56px", lineHeight: 1.1, letterSpacing: "-0.02em" }}
            >
              Let's talk.
            </h1>
            <p
              className="mx-auto mt-6 font-sans text-cream-50/85 leading-relaxed"
              style={{ maxWidth: "540px", fontSize: "16px" }}
            >
              We respond to every inquiry within 24 hours. Whether you're planning for your family or building a Strategist practice, we'd like to hear from you.
            </p>
            <div className="mt-8 flex flex-col items-center gap-2">
              <p
                className="font-sans font-semibold uppercase text-bronze-400"
                style={{ fontSize: "10px", letterSpacing: "0.22em" }}
              >
                Fastest way to reach us
              </p>
              <a
                href="mailto:currencythoughtsllc@gmail.com"
                className="inline-flex items-center gap-3 border border-bronze-600 bg-bronze-600 px-6 py-3 font-sans font-semibold text-navy-900 transition-colors hover:bg-bronze-400"
                style={{ borderRadius: "2px", letterSpacing: "0.05em", fontSize: "15px" }}
              >
                <Mail size={18} aria-hidden />
                currencythoughtsllc@gmail.com
              </a>
            </div>
          </div>
        </section>

        {/* SECTION 2 — Two columns */}
        <section className="bg-cream-50 py-20">
          <div className="mx-auto max-w-6xl px-6 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8">
            <ContactInfo />
            <ContactForm />
          </div>

          {/* FAQ */}
          <div className="mx-auto max-w-3xl px-6 mt-24">
            <div className="text-center">
              <Eyebrow color="bronze" align="center">Common Questions</Eyebrow>
              <h2
                className="mt-6 font-display font-semibold text-navy-800"
                style={{ fontSize: "36px", letterSpacing: "-0.01em" }}
              >
                Before you reach out.
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-bronze-600/30"
                >
                  <AccordionTrigger className="text-left font-display text-[18px] text-navy-800 hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="font-sans text-charcoal-500 leading-relaxed"
                    style={{ fontSize: "14px" }}
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ContactInfo() {
  return (
    <div
      className="bg-white p-8"
      style={{ borderRadius: "4px", boxShadow: "0 10px 40px -10px rgba(168,139,92,0.18)" }}
    >
      <h3
        className="font-display font-semibold text-navy-800"
        style={{ fontSize: "24px" }}
      >
        Reach us directly
      </h3>
      <a
        href="mailto:currencythoughtsllc@gmail.com"
        className="mt-6 flex items-center gap-3 border border-bronze-600/50 bg-cream-50 px-4 py-4 font-sans font-semibold text-navy-800 break-all transition-colors hover:border-bronze-600 hover:bg-bronze-400/15"
        style={{ borderRadius: "3px", fontSize: "15px" }}
      >
        <Mail size={18} className="shrink-0 text-bronze-600" aria-hidden />
        currencythoughtsllc@gmail.com
      </a>
      <div className="mt-8 flex flex-col gap-5">
        <InfoRow icon={<Clock size={18} />} label="Response Time" value="Within 24 hours, every weekday." />
        <InfoRow icon={<Calendar size={18} />} label="Office Hours" value="Mon–Fri · 9:00am – 6:00pm ET" />
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-bronze-600 mt-1" aria-hidden>{icon}</span>
      <div>
        <p
          className="font-sans font-semibold uppercase text-charcoal-500"
          style={{ fontSize: "10px", letterSpacing: "0.22em" }}
        >
          {label}
        </p>
        <p className="mt-1 font-sans text-navy-800" style={{ fontSize: "14px" }}>
          {value}
        </p>
      </div>
    </div>
  );
}

const contactSchema = z.object({
  full_name: z.string().trim().min(1, "Full name is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  topic: z.enum(["General", "Protection", "Retirement", "Legacy & Estate", "Become a Strategist", "Other"]),
  message: z.string().trim().min(20, "Please share at least a few sentences (20+ characters).").max(2000),
});

type ContactValues = z.infer<typeof contactSchema>;

function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { topic: "General" },
  });

  const onSubmit = async (values: ContactValues) => {
    setSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      full_name: values.full_name,
      email: values.email,
      subject: values.topic,
      body: values.message,
      status: "new",
    });
    if (error) {
      setSubmitting(false);
      toast.error("Something went wrong. Please try again.");
      return;
    }
    await supabase.from("activity_logs").insert({
      action: "contact_message_submitted",
      target_type: "contact_messages",
      metadata: { topic: values.topic, email: values.email },
    });
    void sendEmail({
      template: "lead_intake",
      to: values.email,
      props: {
        firstName: values.full_name.split(/\s+/)[0] || "there",
        requestType: values.topic,
        submittedAt: new Date().toISOString(),
      },
    });
    setSubmitting(false);
    toast.success("Message received. We'll be in touch within 24 hours.");
    trackEvent("lead_submitted", { source: "contact", topic: values.topic });
    reset();
  };

  return (
    <div
      className="bg-white p-8"
      style={{ borderRadius: "4px", boxShadow: "0 10px 40px -10px rgba(168,139,92,0.18)" }}
    >
      <h3 className="font-display font-semibold text-navy-800" style={{ fontSize: "24px" }}>
        Send a message
      </h3>
      <p className="mt-3 font-sans text-[13px] leading-relaxed text-charcoal-500">
        Prefer email? Write us directly at{" "}
        <a
          href="mailto:currencythoughtsllc@gmail.com"
          className="font-semibold text-navy-800 underline decoration-bronze-600 underline-offset-4 break-all"
        >
          currencythoughtsllc@gmail.com
        </a>
        . Messages sent through this form land in our inbox either way — a team member reviews every one.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
        <FieldWrap label="Full Name" error={errors.full_name?.message}>
          <input
            type="text"
            {...register("full_name")}
            className={inputCls}
            autoComplete="name"
          />
        </FieldWrap>
        <FieldWrap label="Email" error={errors.email?.message}>
          <input
            type="email"
            {...register("email")}
            className={inputCls}
            autoComplete="email"
          />
        </FieldWrap>
        <FieldWrap label="Topic" error={errors.topic?.message}>
          <select {...register("topic")} className={inputCls}>
            <option>General</option>
            <option>Protection</option>
            <option>Retirement</option>
            <option>Legacy &amp; Estate</option>
            <option>Become a Strategist</option>
            <option>Other</option>
          </select>
        </FieldWrap>
        <FieldWrap label="Message" error={errors.message?.message}>
          <textarea
            rows={5}
            {...register("message")}
            className={inputCls}
          />
        </FieldWrap>
        <PrimaryButton type="submit" size="lg" disabled={submitting} className="w-full">
          {submitting ? "Sending…" : "Send Message"}
        </PrimaryButton>
      </form>
    </div>
  );
}

const inputCls =
  "w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none";

function FieldWrap({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="block font-sans font-semibold uppercase text-charcoal-500"
        style={{ fontSize: "10px", letterSpacing: "0.22em" }}
      >
        {label}
      </label>
      <div className="mt-2" style={{ borderRadius: "4px", overflow: "hidden" }}>
        {children}
      </div>
      {error && (
        <p className="mt-1 font-sans text-[12px] text-[color:var(--color-danger)]">{error}</p>
      )}
    </div>
  );
}

const FAQ = [
  {
    q: "What does Currency Thoughts actually do?",
    a: "We're an independent insurance agency focused on generational wealth for families. We teach the mechanics of protection, accumulation, and transfer, and we stay in the relationship long after the paperwork.",
  },
  {
    q: "Are you affiliated with a single carrier?",
    a: "No. We're independent, and we are not limited to one company's shelf. We identify the companies we are appointed with directly in consultation rather than in public advertising.",
  },
  {
    q: "Is my consultation really free?",
    a: "Yes. The 30-minute consultation is complimentary and educational. We'll reach out to schedule a 30-minute conversation about your family's situation. There's no obligation to move forward.",
  },
  {
    q: "What products do you specialize in?",
    a: "We work on four needs: what happens if household income stops, how value moves to the next generation intact, how accumulated value becomes reliable income, and how a business stays whole through an owner's exit. The specific strategies that fit your family are discussed in a personal consultation, where we can review your actual situation.",
  },
  {
    q: "How do I become a Legacy Strategist?",
    a: "Visit our Careers page to start an application. We look for principled, coachable, family-minded professionals. Training, mentorship, and modern technology are included — a legacy mindset is required.",
  },
  {
    q: "Where do you serve clients?",
    a: "We serve families across the United States in states where our Strategists are properly licensed. During your consultation we'll confirm availability for your state and household.",
  },
] as const;